import { useEffect, useRef, type ReactNode } from "react";
import styles from "./Modal.module.css";
import { Button } from "./Button.js";
import { Heading } from "./Text.js";

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Wide enough for a preview table rather than a confirmation. */
  size?: "base" | "wide";
}

/**
 * The platform's `<dialog>`, not a div with a z-index.
 *
 * It brings the focus trap, the Escape key, inertness of the page behind it
 * and the top layer for free — all of which are exactly the parts a
 * hand-rolled modal gets wrong, and none of which are worth a dependency.
 *
 * On a phone it is a sheet: full width, anchored to the bottom, because a
 * centred box with a keyboard open is unusable.
 */
export function Modal({ open, title, onClose, children, footer, size = "base" }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={size === "wide" ? `${styles.dialog} ${styles.wide}` : styles.dialog}
      // Escape and the backdrop both mean the same thing, and both have to
      // tell React, or the next open() is a no-op.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      aria-label={title}
    >
      <div className={styles.body}>
        <header className={styles.header}>
          <Heading level={2} size="lg">
            {title}
          </Heading>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </header>
        <div className={styles.content}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </dialog>
  );
}
